import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart } from "../cartContext";
import { useAuth } from "../authContext";
import axios from "../lib/axios";
import ConfettiBurst, { createConfettiPieces } from "../components/ConfettiBurst";

const GST_RATE = 18;
const GST_NUMBER_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const OTP_PLACEHOLDER_EMAIL_SUFFIX = "@otp.indorinama.invalid";
const OTP_PLACEHOLDER_NAME_PREFIX = "Indorinama User";
const OTP_PLACEHOLDER_CITY = "unknown";
const REQUIRED_QUOTATION_FIELDS = new Set([
  "name",
  "email",
  "phone",
  "city",
  "company_name",
]);

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const normalizePhone = (value = "") => value.replace(/\D/g, "").slice(0, 10);
const normalizeEmail = (value = "") => value.trim().toLowerCase();

const normalizeGST = (value = "") =>
  value
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, 15);

const getItemDescription = (item) =>
  typeof item?.description === "string" && item.description.trim()
    ? item.description.trim()
    : "";

const getQuantityEntries = (quantity) => {
  if (!quantity) return [];

  const parsedQuantity =
    typeof quantity === "string"
      ? (() => {
          try {
            return JSON.parse(quantity);
          } catch (error) {
            return quantity;
          }
        })()
      : quantity;

  if (typeof parsedQuantity === "object" && !Array.isArray(parsedQuantity)) {
    return Object.entries(parsedQuantity);
  }

  return [["quantity", parsedQuantity]];
};

const getItemSupportingText = (item) => {
  const description = getItemDescription(item);
  if (description) return description;

  return getQuantityEntries(item?.quantity)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `${String(key).replace(/_/g, " ")}: ${value}`)
    .join(" • ");
};

const getItemImage = (item) => item?.image_url || "";

const getItemInitials = (item) =>
  String(item?.name || "IN")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const isOtpPlaceholderEmail = (value = "") =>
  normalizeEmail(value).endsWith(OTP_PLACEHOLDER_EMAIL_SUFFIX);

const isOtpPlaceholderName = (value = "") =>
  value.trim().startsWith(OTP_PLACEHOLDER_NAME_PREFIX);

const isOtpPlaceholderCity = (value = "") =>
  value.trim().toLowerCase() === OTP_PLACEHOLDER_CITY;

const getProfilePrefillValue = (value, isPlaceholder) =>
  isPlaceholder(value) ? "" : value || "";

const validateField = (name, value) => {
  switch (name) {
    case "name":
      return value.trim() === ""
        ? "Name is required"
        : !/^[a-zA-Z\s]+$/.test(value.trim())
          ? "Name should only contain letters"
          : "";
    case "email":
      return value.trim() === ""
        ? "Email is required"
        : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? "Please enter a valid email address"
          : "";
    case "phone":
      return value.trim() === ""
        ? "Phone number is required"
        : !/^\d{10}$/.test(normalizePhone(value))
          ? "Phone number must be 10 digits"
          : "";
    case "city":
      return value.trim() === ""
        ? "City is required"
        : !/^[a-zA-Z\s]+$/.test(value.trim())
          ? "City should only contain letters"
          : "";
    case "company_name":
      return value.trim() === "" ? "Company name is required" : "";
    case "gst_number":
      return value.trim() && !GST_NUMBER_PATTERN.test(normalizeGST(value))
        ? "Enter a valid 15-character GST number"
        : "";
    default:
      return "";
  }
};

const Cart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, accessToken, updateUser } = useAuth();
  const { cartItems, loading, removeFromCart, fetchCartItems } = useCart();
  const userId = user?.id || user?._id;

  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [quotationData, setQuotationData] = useState({
    billing_details: {
      name: "",
      email: "",
      phone: "",
      city: "",
      company_name: "",
      gst_number: "",
    },
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    company_name: "",
    gst_number: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [successType, setSuccessType] = useState(null);
  const [showCompletionConfetti, setShowCompletionConfetti] = useState(false);
  const [completionConfettiPieces, setCompletionConfettiPieces] = useState([]);
  const completionConfettiTimeoutRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    setQuotationData((prev) => ({
      ...prev,
      billing_details: {
        ...prev.billing_details,
        name: getProfilePrefillValue(user.name, isOtpPlaceholderName),
        email: getProfilePrefillValue(user.email, isOtpPlaceholderEmail),
        phone: normalizePhone(user.phone || ""),
        city: getProfilePrefillValue(user.city, isOtpPlaceholderCity),
      },
    }));
  }, [user]);

  useEffect(() => {
    if (showQuotationForm) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showQuotationForm]);

  useEffect(() => {
    const routeSuccessType =
      location.state?.successType || location.state?.completionType;

    if (!routeSuccessType) return;

    setSuccessType(routeSuccessType);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!successType) return;

    setCompletionConfettiPieces(
      createConfettiPieces(96, {
        leftRange: [5, 95],
        topRange: [2, 18],
        xRange: [-320, 320],
        yRange: [220, 460],
      }),
    );
    setShowCompletionConfetti(true);

    if (completionConfettiTimeoutRef.current) {
      window.clearTimeout(completionConfettiTimeoutRef.current);
    }

    completionConfettiTimeoutRef.current = window.setTimeout(() => {
      setShowCompletionConfetti(false);
      setSuccessType(null);
    }, 3000);

    return () => {
      if (completionConfettiTimeoutRef.current) {
        window.clearTimeout(completionConfettiTimeoutRef.current);
      }
    };
  }, [successType]);

  const totals = useMemo(() => {
    const subtotal = cartItems.reduce(
      (sum, item) => sum + Number(item?.subtotal || 0),
      0,
    );
    const taxAmount = cartItems.reduce(
      (sum, item) => sum + Number(item?.tax_amount || 0),
      0,
    );
    const discountAmount = cartItems.reduce(
      (sum, item) => sum + Number(item?.discount_amount || 0),
      0,
    );
    const totalAmount = cartItems.reduce(
      (sum, item) => sum + Number(item?.total_amount || 0),
      0,
    );

    return {
      subtotal,
      tax_amount: taxAmount,
      discount_amount: discountAmount,
      taxable_value: subtotal - discountAmount,
      total_amount: totalAmount,
    };
  }, [cartItems]);

  const handleContinueShopping = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/advertising");
  };

  const handleRemoveItem = async (cartId) => {
    try {
      await removeFromCart(cartId);
      toast.success("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const handleInputChange = (event) => {
    const { name } = event.target;
    let { value } = event.target;

    if (name === "phone") {
      value = normalizePhone(value);
    }

    if (name === "gst_number") {
      value = normalizeGST(value);
    }

    setQuotationData((prev) => ({
      ...prev,
      billing_details: {
        ...prev.billing_details,
        [name]: value,
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  const validateQuotationForm = () => {
    const billingDetails = quotationData.billing_details;
    const nextErrors = {
      name: validateField("name", billingDetails.name),
      email: validateField("email", billingDetails.email),
      phone: validateField("phone", billingDetails.phone),
      city: validateField("city", billingDetails.city),
      company_name: validateField("company_name", billingDetails.company_name),
      gst_number: validateField("gst_number", billingDetails.gst_number || ""),
    };

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleQuotationSubmit = async (event) => {
    event.preventDefault();

    if (!validateQuotationForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    try {
      setFormLoading(true);

      const billingDetails = quotationData.billing_details;
      const quotationPayload = {
        user: userId,
        cart_items: cartItems.map((item) => ({
          quantity: item.quantity,
          name: item.name,
          description: getItemDescription(item),
          subtotal: item.subtotal,
          tax_amount: item.tax_amount,
          total_amount: item.total_amount,
        })),
        billing_details: {
          name: billingDetails.name.trim(),
          email: billingDetails.email.trim(),
          phone: normalizePhone(billingDetails.phone),
          city: billingDetails.city.trim(),
          company_name: billingDetails.company_name.trim(),
          gst_number: billingDetails.gst_number
            ? normalizeGST(billingDetails.gst_number)
            : undefined,
        },
        ...totals,
        discount: totals.discount_amount,
        created_at: new Date(),
        updated_at: new Date(),
      };

      const response = await axios.post("/api/quotation", quotationPayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.data) {
        throw new Error("Failed to create quotation");
      }

      if (user && typeof updateUser === "function") {
        try {
          const profileResponse = await axios.get("/api/users/me", {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });

          const refreshedUser =
            profileResponse.data?.user ??
            profileResponse.data?.doc ??
            profileResponse.data ??
            null;

          if (refreshedUser) {
            updateUser(refreshedUser);
          }
        } catch (profileError) {
          console.error("Failed to refresh user profile after quotation submit", profileError);
        }
      }

      await fetchCartItems?.();
      setShowQuotationForm(false);
      setSuccessType("quotation");

      window.setTimeout(() => {
        navigate("/my-orders");
      }, 1800);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to create quotation",
      );
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-red-600"></div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ConfettiBurst
        active={showCompletionConfetti}
        pieces={completionConfettiPieces}
        className="fixed inset-0 z-[90]"
        portal
      />
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
            <span className="text-gray-500">
              {cartItems?.length || 0} {cartItems?.length === 1 ? "item" : "items"}
            </span>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {!cartItems?.length ? (
                <div className="rounded-2xl bg-white p-8 text-center shadow-md">
                  <div className="mb-6">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-lg font-medium text-gray-900">
                    Your cart is empty
                  </h3>
                  <p className="mb-6 text-gray-500">
                    Looks like you have not added any items to your cart yet.
                  </p>
                  <button
                    onClick={handleContinueShopping}
                    className="inline-flex items-center rounded-full bg-black px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 overflow-hidden rounded-2xl bg-white shadow-md">
                  {cartItems.map((item, index) => {
                    const itemDiscount = Number(item?.discount_amount || 0);
                    const itemTaxableValue = Number(item?.subtotal || 0) - itemDiscount;
                    const itemSupportingText = getItemSupportingText(item);
                    const itemImage = getItemImage(item);

                    return (
                      <div
                        key={item.id}
                        className={`p-6 ${index === cartItems.length - 1 ? "" : "border-b border-gray-200"}`}
                      >
                        <div className="relative">
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="absolute right-0 top-0 z-10 rounded-full p-1 text-gray-400 transition-colors hover:text-red-600"
                            aria-label={`Remove ${item.name}`}
                          >
                            <FaTimes className="text-lg" />
                          </button>

                          <div className="flex flex-col gap-4 pr-8 md:flex-row md:items-start md:justify-between">
                            <div className="flex min-w-0 flex-1 items-start gap-3 md:gap-4">
                              <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-gray-100 sm:h-20 sm:w-20">
                                {itemImage ? (
                                  <img
                                    src={itemImage}
                                    alt={item.name}
                                  className="h-full w-full object-cover object-center"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center bg-gray-200">
                                    <span className="text-sm font-bold uppercase tracking-[0.14em] text-gray-600">
                                      {getItemInitials(item)}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <div className="min-w-0 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {item.name}
                                </h3>
                                {itemSupportingText && (
                                  <p
                                    className="mt-1 max-w-2xl text-sm leading-6 text-gray-500"
                                  >
                                    {itemSupportingText}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 rounded-2xl bg-gray-50 p-3 text-sm md:min-w-[250px] md:bg-transparent md:p-0 md:text-right">
                              <div className="contents">
                                <span className="text-gray-600">Subtotal</span>
                                <span className="text-right text-gray-700">
                                  ₹ {formatCurrency(item.subtotal)}
                                </span>
                              </div>
                              {itemDiscount > 0 && (
                                <div className="contents">
                                  <span className="text-green-600">Discount</span>
                                  <span className="text-right text-green-600">
                                    - ₹ {formatCurrency(itemDiscount)}
                                  </span>
                                </div>
                              )}
                              <div className="contents">
                                <span className="text-gray-600">Taxable</span>
                                <span className="text-right text-gray-700">
                                  ₹ {formatCurrency(itemTaxableValue)}
                                </span>
                              </div>
                              <div className="contents">
                                <span className="text-gray-600">GST</span>
                                <span className="text-right text-gray-700">
                                  ₹ {formatCurrency(item.tax_amount || 0)}
                                </span>
                              </div>
                              <div className="col-span-2 mt-1 border-t border-gray-200 pt-2 text-right text-lg font-semibold text-gray-900">
                                ₹ {formatCurrency(item.total_amount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {cartItems?.length > 0 && (
              <div className="lg:col-span-1">
                <div className="sticky top-4 rounded-2xl bg-white p-6 shadow-md">
                  <h2 className="mb-6 text-lg font-semibold text-gray-900">
                    Order Summary
                  </h2>

                  <div className="space-y-4">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal</span>
                      <span>₹ {formatCurrency(totals.subtotal)}</span>
                    </div>
                    {totals.discount_amount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount</span>
                        <span>- ₹ {formatCurrency(totals.discount_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Taxable Value</span>
                      <span>₹ {formatCurrency(totals.taxable_value)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>GST ({GST_RATE}%)</span>
                      <span>₹ {formatCurrency(totals.tax_amount)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between text-base font-semibold text-gray-900">
                        <span>Total</span>
                        <span>₹ {formatCurrency(totals.total_amount)}</span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        GST is applied on the taxable value after discount.
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowQuotationForm(true)}
                    className="mt-6 flex w-full items-center justify-center rounded-xl bg-red-600 px-4 py-3 font-medium text-white transition-colors hover:bg-red-700"
                  >
                    Get Quotation
                  </button>
                  <button
                    onClick={handleContinueShopping}
                    className="mt-4 flex w-full items-center justify-center rounded-xl bg-black px-4 py-3 font-medium text-white transition-colors hover:bg-gray-800"
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />

      {showQuotationForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/65 p-3 sm:p-4 backdrop-blur-sm">
          <div className="relative flex flex-col w-full max-w-2xl max-h-[90vh] rounded-[22px] bg-white shadow-2xl overflow-hidden sm:rounded-2xl">
            <form onSubmit={handleQuotationSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="shrink-0 border-b border-gray-200 bg-black px-4 py-3.5 sm:px-6 flex items-center justify-between gap-3 text-white">
                <h4 className="min-w-0 flex-1 truncate text-left text-sm font-bold text-white sm:text-base">
                  Quotation Request
                </h4>
                <div className="flex shrink-0 items-center gap-2">
                  <img src="/in-logo-full.svg" alt="Indorinama Logo" className="h-8 sm:h-12 object-contain" />
                  <button
                    type="button"
                    aria-label="Close"
                    className="rounded-full bg-white/10 p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
                    onClick={() => setShowQuotationForm(false)}
                  >
                    <FaTimes className="text-sm" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Full Name
                      {REQUIRED_QUOTATION_FIELDS.has("name") ? (
                        <span className="ml-1 text-red-500">*</span>
                      ) : null}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={quotationData.billing_details.name}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl border px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                        errors.name ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your full name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Email Address
                      {REQUIRED_QUOTATION_FIELDS.has("email") ? (
                        <span className="ml-1 text-red-500">*</span>
                      ) : null}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={quotationData.billing_details.email}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl border px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your email"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Phone Number
                      {REQUIRED_QUOTATION_FIELDS.has("phone") ? (
                        <span className="ml-1 text-red-500">*</span>
                      ) : null}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={quotationData.billing_details.phone}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl border px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                        errors.phone ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your 10-digit phone number"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      City
                      {REQUIRED_QUOTATION_FIELDS.has("city") ? (
                        <span className="ml-1 text-red-500">*</span>
                      ) : null}
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={quotationData.billing_details.city}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl border px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                        errors.city ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your city"
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-500">{errors.city}</p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Company Name
                      {REQUIRED_QUOTATION_FIELDS.has("company_name") ? (
                        <span className="ml-1 text-red-500">*</span>
                      ) : null}
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={quotationData.billing_details.company_name}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl border px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                        errors.company_name
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Example: Acme Media 24"
                    />
                    {errors.company_name && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.company_name}
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      GST Number
                    </label>
                    <input
                      type="text"
                      name="gst_number"
                      value={quotationData.billing_details.gst_number}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl border px-4 py-3 uppercase text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                        errors.gst_number
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="27ABCDE1234F1Z5"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Format: 15 characters, for example{" "}
                      <span className="font-medium">27ABCDE1234F1Z5</span>
                    </p>
                    {errors.gst_number && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.gst_number}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="shrink-0 flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="space-y-0.5 text-xs leading-5 text-gray-600 sm:text-sm">
                  <p className="pr-2">
                    Support:{" "}
                    <a
                      href="mailto:help@indorinama.in"
                      className="font-medium text-red-600 transition-colors hover:text-red-800"
                    >
                      help@indorinama.in
                    </a>
                  </p>
                  <p className="pr-2">
                    Call Us:{" "}
                    <a
                      href="tel:+918989801088"
                      className="font-medium text-red-600 transition-colors hover:text-red-800"
                    >
                      +91 89898 01088
                    </a>
                  </p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                  <button
                    type="button"
                    className="w-full rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 sm:w-auto"
                    onClick={() => setShowQuotationForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex w-full items-center justify-center rounded-lg bg-black px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:opacity-50 sm:w-auto"
                    disabled={formLoading}
                  >
                    {formLoading ? (
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        Processing...
                      </>
                    ) : (
                      "Submit Quotation"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Cart;
